import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { Input } from '../components/ui/input';
import { DataPagination } from '../components/DataPagination';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Wallet, MagnifyingGlass, Spinner, ArrowsClockwise, Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const _num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const _fmt = (v, opts = {}) => {
  const n = _num(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2, ...opts });
};

const _fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function CashierFloatLogsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (q.trim()) params.set('q', q.trim());
      const { data } = await axios.get(
        `${API_URL}/api/cashiers/float-logs${params.toString() ? `?${params}` : ''}`,
        { headers: authHeaders() },
      );
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load float logs'));
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setCurrentPage(1); }, [q, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleRefresh = () => {
    setRefreshing(true);
    load(true);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="cashier-float-logs-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0A2540] flex items-center justify-center">
            <Wallet size={22} weight="duotone" className="text-[#C9A35B]" />
          </div>
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.2em] text-[#78716C] font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Cashiers · Float Logs
            </p>
            <h1
              className="text-2xl font-bold text-[#1C1917] mt-0.5"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Agent Float Logs
            </h1>
            <p className="text-sm text-[#78716C] mt-1">
              View daily float assignments and usage.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="float-logs-refresh"
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold uppercase tracking-wider text-[#0A2540] hover:bg-[#FCFAF6] transition"
        >
          {refreshing
            ? <Spinner size={14} className="animate-spin" />
            : <ArrowsClockwise size={14} weight="bold" />}
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <FieldGroup label="Search">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Agent, mobile, campus, control no…"
              className="h-9 pl-8 text-sm w-[280px]"
              data-testid="float-logs-search"
            />
          </div>
        </FieldGroup>
        <FieldGroup label="From">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 text-sm w-[160px]"
            data-testid="float-logs-from"
          />
        </FieldGroup>
        <FieldGroup label="To">
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 text-sm w-[160px]"
            data-testid="float-logs-to"
          />
        </FieldGroup>
      </div>

      {/* Table — trimmed to Date · Agent · Assigned · Recharge · Sub Reg · Renewal · Used · Remaining · Revenue · View */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FCFAF6] hover:bg-[#FCFAF6]">
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Agent</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Assigned</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Recharge</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Sub. Reg</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Renewal</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Used</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Remaining</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Revenue</TableHead>
                <TableHead className="w-[64px] text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12">
                  <Spinner size={20} className="animate-spin text-[#0A2540] inline" />
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-sm text-[#78716C]">
                  No float logs found for the current filter.
                </TableCell></TableRow>
              ) : pagedRows.map((r) => (
                <TableRow
                  key={r.float_id}
                  className="hover:bg-[#FCFAF6]/50 cursor-pointer"
                  data-testid={`float-log-row-${r.float_id}`}
                  onClick={() => setDetailRow(r)}
                >
                  <TableCell className="text-xs font-mono text-[#78716C]">{_fmtDate(r.float_date)}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-[#1C1917]">{r.agent_name || '—'}</p>
                  </TableCell>
                  <TableCell className="text-right text-sm text-[#1C1917] font-semibold">{_fmt(r.assigned_float)}</TableCell>
                  <TableCell className="text-right text-sm text-[#1C1917]">{_fmt(r.used_float_card_recharge)}</TableCell>
                  <TableCell className="text-right text-sm text-[#1C1917]">{_fmt(r.used_float_sub_registration)}</TableCell>
                  <TableCell className="text-right text-sm text-[#1C1917]">{_fmt(r.used_float_card_renewal)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-[#C9A35B]">{_fmt(r.total_used_float)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-[#10B981]">{_fmt(r.remained_float)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-[#0A2540]">{_fmt(r.total_revenue)}</TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDetailRow(r); }}
                      data-testid={`float-log-view-${r.float_id}`}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#EDE6D8] bg-white hover:bg-[#FCFAF6] text-[#0A2540] transition"
                      aria-label="View details"
                    >
                      <Eye size={14} weight="bold" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <DataPagination
        page={safePage}
        pageSize={pageSize}
        total={rows.length}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={(n) => { setPageSize(n); setCurrentPage(1); }}
        testId="float-logs-pagination"
      />

      <FloatLogDetailDialog row={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="space-y-1">
      <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">{label}</span>
      {children}
    </div>
  );
}

function StatusBadge({ value }) {
  const v = String(value || '').trim();
  if (!v) return <span className="text-[11px] text-[#78716C]">—</span>;
  const isPaid = /paid|settled|success/i.test(v);
  const isPending = /pend/i.test(v);
  const bg = isPaid ? '#E7F5EC' : isPending ? '#FEF3C7' : '#FBE7E7';
  const fg = isPaid ? '#047857' : isPending ? '#A16207' : '#B91C1C';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: bg, color: fg }}
    >
      {v}
    </span>
  );
}

/* ─── Row detail dialog ─────────────────────────────────────────────────── */
function FloatLogDetailDialog({ row, onClose }) {
  const open = !!row;
  const r = row || {};
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="float-log-detail-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Float Log · {_fmtDate(r.float_date)}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Full detail for the selected float assignment.
          </DialogDescription>
        </DialogHeader>

        {/* Agent & context block */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-1">
          <Field label="Agent" value={r.agent_name} />
          <Field label="Mobile" mono value={r.mobile} />
          <Field label="Campus" value={r.campus_name} />
          <Field label="Entity" value={r.entity_name} />
          <Field label="Control #" mono value={r.control_number} />
          <Field label="Float ID" mono value={r.float_id} />
          <Field label="Payment Status">
            <StatusBadge value={r.payment_status} />
          </Field>
        </div>

        <div className="border-t border-[#EDE6D8] my-2" />

        {/* Money block */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Assigned Float" value={_fmt(r.assigned_float)} strong />
          <Field label="Total Used" value={_fmt(r.total_used_float)} strong accent="#C9A35B" />
          <Field label="Used · Card Recharge" value={_fmt(r.used_float_card_recharge)} />
          <Field label="Used · Sub. Registration" value={_fmt(r.used_float_sub_registration)} />
          <Field label="Used · Card Renewal" value={_fmt(r.used_float_card_renewal)} />
          <Field label="Used · Bottle Sell" value={_fmt(r.used_float_bottle_sell)} />
          <Field label="Subscription Fee" value={_fmt(r.subscription_fee_amt)} />
          <Field label="Remaining Float" value={_fmt(r.remained_float)} strong accent="#10B981" />
          <Field label="Total Revenue" value={_fmt(r.total_revenue)} strong accent="#0A2540" />
        </div>

        <div className="border-t border-[#EDE6D8] my-2" />

        {/* Activity counts block */}
        <div>
          <p
            className="text-[10px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold mb-2"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Activity Counts
          </p>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3" data-testid="float-log-activity-counts">
            <Field label="Paid Plan Subs" mono value={_fmt(r.paid_plan_sub, { maximumFractionDigits: 0 })} />
            <Field label="Free Plan Subs" mono value={_fmt(r.free_plan_sub, { maximumFractionDigits: 0 })} />
            <Field label="Paid Recharges" mono value={_fmt(r.paid_recharge_cnt, { maximumFractionDigits: 0 })} />
            <Field label="Offer Recharges" mono value={_fmt(r.offer_recharge_cnt, { maximumFractionDigits: 0 })} />
            <Field label="New Cards Sold" mono value={_fmt(r.card_new_sold, { maximumFractionDigits: 0 })} />
            <Field label="Renewal Cards Sold" mono value={_fmt(r.card_renewal_sold, { maximumFractionDigits: 0 })} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono, strong, accent, children }) {
  return (
    <div className="min-w-0">
      <p
        className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {label}
      </p>
      {children
        ? <div className="mt-1">{children}</div>
        : (
          <p
            className={`mt-0.5 text-sm ${strong ? 'font-semibold' : ''} ${mono ? 'font-mono' : ''} truncate`}
            style={{ color: accent || '#1C1917' }}
          >
            {value === null || value === undefined || value === '' ? '—' : String(value)}
          </p>
        )
      }
    </div>
  );
}
